/**
 * Sky‑related helpers: sunrise/sunset, solar elevation, cloud factor and a
 * simple two‑time‑constant EWMA filter that smooths cloud‑cover readings.
 *
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/sky_condition.test.ts
 */

import { addDate, convertDateFormat, dateFormat, getUTCOffset } from 'src/helpers/date_helper';
import type { GetStatsPeriodRequest } from 'src/types/requests/charts/get_stats_period';

export type SkyConditions = 'CLEAR' | 'PARTLY_CLOUDY' | 'CLOUDY' | 'NIGHT' | 'DAWN' | 'DUSK';

export type SkyConditionsInfo = {
	cloudFactor: number;
	cloudFactorClear: number;
	dawn: string;
	dusk: string;
	nextSunState: string;
	nextSunStateTime: string;
	sky: SkyConditions;
	sunElevDeg: number;
	sunElevSin: number;
	sunrise: string;
	sunriseTime: Date;
	sunset: string;
	sunsetTime: Date;
	time: string;
	timelocal: string;
	timezone: string;
	tzOffset: string;
	datetime: Date;
	lat: number;
	lng: number;
};

export class SkyCondition {
	public static readonly NIGHT: SkyConditions = 'NIGHT';
	public static readonly DAWN: SkyConditions = 'DAWN';
	public static readonly DUSK: SkyConditions = 'DUSK';
	public static readonly CLEAR: SkyConditions = 'CLEAR';
	public static readonly PARTLY_CLOUDY: SkyConditions = 'PARTLY_CLOUDY';
	public static readonly CLOUDY: SkyConditions = 'CLOUDY';

	private static readonly CF_CLEAR_ZENITH = 0.65;
	private static readonly CF_CLEAR_MIN = 0.47;
	private static readonly CF_PARTLY = 0.35;

	/** Latitude in decimal degrees */
	private readonly latDeg: number;
	/** Longitude in decimal degrees (east‑positive) */
	private readonly lonDeg: number;
	/** Time‑zone offset in **hours** (e.g. +2 for Europe/Sofia) */
	private readonly tzOffset: {
		hours: number;
		minutes: number;
		offsetTime: string;
	};
	private readonly timezone: string;

	constructor(latDeg: number, lonDeg: number, timezone: string) {
		this.latDeg = latDeg;
		this.lonDeg = lonDeg;
		this.timezone = timezone;
		this.tzOffset = getUTCOffset(timezone);
	}
	public static adaptiveClearThreshold = (sunElevSin: number) => {
		return Math.max(
			SkyCondition.CF_CLEAR_MIN,
			SkyCondition.CF_CLEAR_ZENITH * sunElevSin + SkyCondition.CF_CLEAR_MIN * (1 - sunElevSin),
		);
	};
	/**
	 * Public, one‑stop method that returns a full snapshot for the given moment.
	 */
	public getSkyInfo(lux: number, uv: number, date: string | number | Date = null): SkyConditionsInfo {
		const d = new Date(date || Date.now());

		// Civil dawn/dusk = zenith 96°
		const civil = this.computeSunTimes(d, 96);
		const sun = this.computeSunTimes(d); // official sunrise/sunset
		const sunElevDeg = this._getSolarElevation(d);
		const skyInfo: SkyConditionsInfo = {
			...sun,
			dawn: civil.sunrise,
			dusk: civil.sunset,
			sky: SkyCondition.CLEAR,
			time: dateFormat(d, 'H:i'),
			timelocal: convertDateFormat(d, this.timezone),
			timezone: this.timezone,
			tzOffset: this.tzOffset.offsetTime,
			nextSunState: 'sunset',
			nextSunStateTime: sun.sunset,
			sunElevDeg,
			sunElevSin: Math.sin((sunElevDeg * Math.PI) / 180),
			cloudFactor: -1,
			cloudFactorClear: 0,
			datetime: d,
			lat: this.latDeg,
			lng: this.lonDeg,
		};
		if (d.getTime() >= sun.sunsetTime.getTime()) {
			skyInfo.nextSunState = 'sunrise';
			// Next solar event is sunrise: use today’s sunrise if we’re still before it (after midnight),
			// otherwise compute sunrise for the next day.
			skyInfo.nextSunStateTime =
				d.getHours() <= sun.sunriseTime.getHours()
					? sun.sunrise
					: this.computeSunTimes(addDate(d, 'h', 24)).sunrise;
			skyInfo.sky =
				d.getTime() > civil.sunsetTime.getTime() ? SkyCondition.NIGHT : SkyCondition.DUSK;
		} else if (d.getTime() <= sun.sunriseTime.getTime()) {
			skyInfo.nextSunState = 'sunrise';
			skyInfo.nextSunStateTime = sun.sunrise;
			skyInfo.sky =
				d.getTime() < civil.sunriseTime.getTime() ? SkyCondition.NIGHT : SkyCondition.DAWN;
		} else {
			skyInfo.cloudFactor = this._computeCloudFactor(lux, uv, skyInfo.sunElevSin);
			skyInfo.cloudFactorClear = SkyCondition.adaptiveClearThreshold(skyInfo.sunElevSin);
			skyInfo.sky = this.cloudFactorToCondition(skyInfo.cloudFactor, skyInfo.cloudFactorClear);
		}

		return skyInfo;
	}
	/** Map numerical cloud‑factor (0…1) to discrete condition */
	public cloudFactorToCondition = (cf: number, cloudFactorClear: number): SkyConditions => {
		if (cf >= cloudFactorClear) return SkyCondition.CLEAR;
		if (cf >= SkyCondition.CF_PARTLY) return SkyCondition.PARTLY_CLOUDY;
		return SkyCondition.CLOUDY;
	};
	/**
	 * Compute sunrise & sunset (local solar time) for a given date.
	 * @param zenithDeg  90.833° = official sunrise/sunset (default),
	 *                   96°     = civil dawn/dusk.
	 */
	public computeSunTimes = (
		date: Date,
		zenithDeg: number = 90.833,
	): {
		sunrise: string;
		sunset: string;
		sunriseTime: Date;
		sunsetTime: Date;
	} => {
		const rad = Math.PI / 180;
		const noon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
		const gamma = this._fractionalYearAngle(noon);
		const decl = this._solarDeclination(gamma);
		const eotMin = this._equationOfTime(gamma); // minutes

		const latRad = this.latDeg * rad;
		const cosH =
			(Math.cos(zenithDeg * rad) - Math.sin(latRad) * Math.sin(decl)) /
			(Math.cos(latRad) * Math.cos(decl));

		if (Math.abs(cosH) > 1) {
			// Polar day/night — no discrete sunrise/sunset
			return {
				sunrise: '00:00',
				sunset: '00:00',
				sunriseTime: noon,
				sunsetTime: noon,
			};
		}

		const hourAngle = Math.acos(cosH) / rad;
		const solarNoon = 720 - 4 * this.lonDeg - eotMin;
		const midnight = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
		const offsetHours = this.tzOffset.hours;
		let sunriseTime = addDate(midnight + (solarNoon - hourAngle * 4) * 60_000, 'h', offsetHours);
		let sunsetTime = addDate(midnight + (solarNoon + hourAngle * 4) * 60_000, 'h', offsetHours);
		if (this.tzOffset.minutes) {
			const minutes = this.tzOffset.minutes * (offsetHours < 0 ? -1 : 1);
			sunriseTime = addDate(sunriseTime, 'i', minutes);
			sunsetTime = addDate(sunsetTime, 'i', minutes);
		}

		return {
			sunrise: dateFormat(sunriseTime, 'H:i'),
			sunset: dateFormat(sunsetTime, 'H:i'),
			sunriseTime,
			sunsetTime,
		};
	};
	/** Solar elevation angle (degrees) at the given date. */
	private _getSolarElevation = (date: Date): number => {
		const rad = Math.PI / 180;
		const gamma = this._fractionalYearAngle(date);
		const decl = this._solarDeclination(gamma);
		const eot = this._equationOfTime(gamma);
		const offsetHours = this.tzOffset.hours;
		const offsetMinutes = this.tzOffset.minutes * (offsetHours < 0 ? -1 : 1);
		const minutesUTC = (date.getHours() - offsetHours) * 60 + (date.getMinutes() - offsetMinutes);
		const trueSolarTimeMin = minutesUTC + eot + 4 * this.lonDeg;

		const hourAngleRad = (trueSolarTimeMin / 4 - 180) * rad;
		const latRad = this.latDeg * rad;

		const sinAlpha =
			Math.sin(latRad) * Math.sin(decl) +
			Math.cos(latRad) * Math.cos(decl) * Math.cos(hourAngleRad);

		return Math.asin(sinAlpha) / rad; // → degrees
	};
	/**
	 * Cloud‑factor – a heuristic 0-1 score, 1 = perfectly clear sky.
	 * Combines lux and UV radiation, normalized for solar elevation.
	 */
	private _computeCloudFactor = (lux: number, uv: number, sunElevSin: number): number => {
		// Very low sun → metrics unreliable; treat as fully cloudy
		if (sunElevSin < 0.05) return 0;

		const REF_UV_CLEAR = 12; // typical clear‑sky UV index at zenith
		const REF_LUX_CLEAR = 120_000; // lux for full sun

		const luxRatio = lux / (REF_LUX_CLEAR * Math.pow(sunElevSin, 1.25));
		const uvRatio = uv / (REF_UV_CLEAR * Math.pow(sunElevSin, 0.9));

		// Cap each ratio at 1.2 then average
		return 0.5 * (Math.min(luxRatio, 1.2) + Math.min(uvRatio, 1.2));
	};
	/** Fractional‑year angle γ (radians). */
	private _fractionalYearAngle(date: Date): number {
		const dayOfYear = this._dayOfYear(date);
		return ((2 * Math.PI) / 365) * (dayOfYear - 1 + (date.getHours() - 12) / 24);
	}
	/** Day of year [1-366]. */
	private _dayOfYear(date: Date): number {
		return Math.floor(
			(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() -
				new Date(date.getFullYear(), 0, 0).getTime()) /
				86_400_000,
		);
	}
	/** Solar declination δ (radians) via the NREL approximation. */
	private _solarDeclination(γ: number): number {
		return (
			0.006918 -
			0.399912 * Math.cos(γ) +
			0.070257 * Math.sin(γ) -
			0.006758 * Math.cos(2 * γ) +
			0.000907 * Math.sin(2 * γ) -
			0.002697 * Math.cos(3 * γ) +
			0.00148 * Math.sin(3 * γ)
		);
	}
	/** Equation of time (minutes) via the NREL approximation. */
	private _equationOfTime(γ: number): number {
		return (
			229.18 *
			(0.000075 +
				0.001868 * Math.cos(γ) -
				0.032077 * Math.sin(γ) -
				0.014615 * Math.cos(2 * γ) -
				0.040849 * Math.sin(2 * γ))
		);
	}
}

/**
 * EWMA (exponential weighted moving average) filter with fast & slow time
 * constants; exposes both values so automation can react quickly while the UI
 * stays smoother unless the difference exceeds a threshold.
 */
export class NormalizeFactorFilter {
	private _fastCF: Ewma;
	private _slowCF: Ewma;
	private _fastSinElev: Ewma;
	private _slowSinElev: Ewma;
	private readonly _skyCond: SkyCondition;

	constructor(skyCondition: SkyCondition) {
		this._skyCond = skyCondition;
	}

	/**
	 * Update the filter with a batch of historical data and return the current cloud status
	 */
	public getCloudStatus(data: GetStatsPeriodRequest['history']): {
		slowCond: SkyConditions;
		fastCond: SkyConditions;
		cond: SkyConditions;
	} {
		let cf0 = 0.5;
		let sin0 = 1.0;
		const last = data.at(-1);
		if (last?.datetime && last?.illuminance != null && last?.uv != null) {
			const skyInfo = this._skyCond.getSkyInfo(last.illuminance, last.uv, last.datetime);
			if (skyInfo && skyInfo.cloudFactor >= 0) {
				cf0 = skyInfo.cloudFactor;
				sin0 = skyInfo.sunElevSin;
			}
		}
		this._initEwma(cf0, sin0);
		data.filter((v) => !v?.missing && v?.illuminance != null && v?.uv != null).forEach((entry) => {
			const date = new Date(entry.datetime);
			const skyInfo = this._skyCond.getSkyInfo(entry.illuminance, entry.uv, date);
			if (skyInfo.cloudFactor >= 0) {
				const cf = skyInfo.cloudFactor;
				const sunElevSin = skyInfo.sunElevSin;
				this._fastCF.update(cf, true);
				this._slowCF.update(cf, true);
				this._fastSinElev.update(sunElevSin, true);
				this._slowSinElev.update(sunElevSin, true);
			}
		});
		return this._currentStatus();
	}
	private _initEwma(initCF: number, initSinElev: number) {
		const tauFastSec = 15 * 60; // 15 minutes
		const tauSlowSec = 2 * 3600; // 2 hours
		const stepSec = 60; // update period (1 minute)
		this._fastCF = new Ewma(tauFastSec, stepSec, initCF);
		this._slowCF = new Ewma(tauSlowSec, stepSec, initCF);
		this._fastSinElev = new Ewma(tauFastSec, stepSec, initSinElev);
		this._slowSinElev = new Ewma(tauSlowSec, stepSec, initSinElev);
	}

	private _currentStatus() {
		const clearFast = SkyCondition.adaptiveClearThreshold(this._fastSinElev.value);
		const clearSlow = SkyCondition.adaptiveClearThreshold(this._slowSinElev.value);
		const fastCond = this._skyCond.cloudFactorToCondition(this._fastCF.value, clearFast);
		const slowCond = this._skyCond.cloudFactorToCondition(this._slowCF.value, clearSlow);
		const cond = Math.abs(this._fastCF.value - this._slowCF.value) > 0.05 ? fastCond : slowCond;
		return { fastCond, slowCond, cond };
	}
}

export type PressureTrend = 'UP' | 'DOWN' | 'STEADY';

export class PressureTrendFilter {
	private _fast: Ewma;
	private _slow: Ewma;

	constructor() {}

	/**
	 * Update the filter with a batch of historical data and return the current pressure trend
	 */
	public getPressureTrend(data: GetStatsPeriodRequest['history']): PressureTrend {
		const series = data
			.filter((d) => d?.min_pressure != null && d?.max_pressure != null && !d?.missing)
			.map((d) => (d.max_pressure + d.min_pressure) / 2)
			.filter((p) => Number.isFinite(p));
		if (series.length < 2) {
			return 'STEADY';
		}
		this._initEwma(series[0]);
		for (const p of series) {
			this._fast.update(p);
			this._slow.update(p);
		}
		return this._currentStatus();
	}

	private _initEwma(initVal: number): void {
		const tauFastSec = 15 * 60; // 15 minutes
		const tauSlowSec = 2 * 3600; // 2 hours
		const stepSec = 60; // update period (1 minute)
		this._fast = new Ewma(tauFastSec, stepSec, initVal);
		this._slow = new Ewma(tauSlowSec, stepSec, initVal);
	}

	private _currentStatus(): PressureTrend {
		const delta = this._fast.value - this._slow.value; // hPa
		const THRESH = 0.3; // under 0.3 hPa → STEADY
		if (Math.abs(delta) < THRESH) {
			return 'STEADY';
		}
		return delta > 0 ? 'UP' : 'DOWN';
	}
}

/**
 * Exponential Weighted Moving Average
 *
 * tauSec   – time constant [s]
 * stepSec  – sampling period [s]
 * initVal  – initial state (default 0)
 */
export class Ewma {
	private readonly _decay: number; // decay coefficient
	private _value: number;

	constructor(tauSec: number, stepSec: number, initVal = 0) {
		this._decay = Math.exp(-stepSec / tauSec);
		this._value = initVal;
	}
	update(x: number, clamp01 = false): number {
		if (clamp01) {
			x = Math.max(0, Math.min(1, x));
		}
		this._value = this._decay * this._value + (1 - this._decay) * x;
		return this._value;
	}
	get value(): number {
		return this._value;
	}
}
